import { useRef, useState } from 'react';

import { Button } from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';

const ACCEPT = 'image/png,image/jpeg,image/webp';
const MAX_INPUT_BYTES = 8 * 1024 * 1024;
const OUTPUT_SIZE = 480;

/**
 * Downscales to a square JPEG data URL.
 *
 * Resizing here rather than on the server is what keeps the stored headshot to
 * a few tens of KB: an 8MB phone photo and a 200KB one both come out the same
 * size, cropped centre-square to match the circular frame on the card.
 */
function toSquareDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.onload = () => {
      const image = new Image();

      image.onerror = () => reject(new Error('That file is not an image we can read.'));
      image.onload = () => {
        const side = Math.min(image.width, image.height);
        const canvas = document.createElement('canvas');
        canvas.width = OUTPUT_SIZE;
        canvas.height = OUTPUT_SIZE;

        const context = canvas.getContext('2d');
        context.drawImage(
          image,
          (image.width - side) / 2,
          (image.height - side) / 2,
          side,
          side,
          0,
          0,
          OUTPUT_SIZE,
          OUTPUT_SIZE,
        );

        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };

      image.src = reader.result;
    };

    reader.readAsDataURL(file);
  });
}

export default function PhotoUpload({ value, onChange }) {
  const inputRef = useRef(null);
  const [isWorking, setIsWorking] = useState(false);

  const pick = async (event) => {
    const file = event.target.files?.[0];
    // Clear immediately so choosing the same file twice still fires a change.
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_INPUT_BYTES) {
      toast.error('That image is over 8MB. Please choose a smaller one.');
      return;
    }

    setIsWorking(true);
    try {
      onChange(await toSquareDataUrl(file));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div className="photo-upload">
      <div className="photo-upload__frame">
        {value ? <img src={value} alt="Agent headshot preview" /> : <span>No photo</span>}
      </div>

      <div className="photo-upload__actions">
        <Button
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          loading={isWorking}
        >
          {value ? 'Replace photo' : 'Upload photo'}
        </Button>

        {value ? (
          <Button variant="ghost" size="sm" onClick={() => onChange('')}>
            Remove
          </Button>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={pick}
          hidden
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}
